import { memo, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { SelectionElementType } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { getTextElementById } from '../utils';
import { updateTextElementProperties, updateTextFontFamily, updateTextElementText } from './textElementPopoverUtils';
import { classnames } from '@/libs/utils';
import FontSelector from '@/Pages/Clip/SideBar/FontSelector/FontSelector';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { brandKit } from '@/stores/brandKit';
import Toggle from '@/components/atoms/Toggle';
import useDebounce from '@/hooks/useDebounce';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import Icon from '@/components/atoms/Icon';

function TextElementTypographyPopover({ textElementId }: { textElementId: string }) {
  const { clipId, clipData } = useClipsContext();
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const usePlatformBrandKit = !!featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit];
  const [activeSubPicker, setActiveSubPicker] = useState<null | 'font' | 'color'>(null);

  const [inputText, setInputText] = useState('');

  const selectedElement = useMemo(() => {
    return getTextElementById(clipData, textElementId);
  }, [clipData, textElementId]);

  useEffect(() => {
    const currentText = selectedElement?.details?.text || '';
    setInputText(currentText);
  }, [selectedElement?.details?.text]);

  const debouncedUpdateText = useDebounce((newText: string) => {
    updateTextElementText(clipId, clipData, textElementId, newText);
  }, 500);

  const onTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const updatedText = e.target.value;
      setInputText(updatedText);
      debouncedUpdateText(updatedText);
    },
    [debouncedUpdateText]
  );

  const onFontSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFontSize = Number(e.target.value);
      updateTextElementProperties(clipId, clipData, textElementId, {
        style: { fontSize: newFontSize }
      });
    },
    [clipId, clipData, textElementId]
  );

  const onTextColorChange = useCallback(
    (value?: string) => {
      if (value) {
        updateTextElementProperties(clipId, clipData, textElementId, {
          style: { color: value }
        });
      }
    },
    [clipId, clipData, textElementId]
  );

  const onFontFamilyChange = useCallback(
    (fontName: string, fontLocation: string) => {
      updateTextFontFamily(clipId, clipData, textElementId, fontName, fontLocation);
    },
    [clipId, clipData, textElementId]
  );

  const toggleTextCaps = useCallback(() => {
    const currentTransform = selectedElement?.style?.textTransform;
    const newTransform = currentTransform === 'uppercase' ? 'lowercase' : 'uppercase';
    updateTextElementProperties(clipId, clipData, textElementId, {
      style: { textTransform: newTransform }
    });
  }, [clipId, clipData, textElementId, selectedElement?.style?.textTransform]);

  const isCapsLock = useMemo(() => {
    return selectedElement?.style?.textTransform === 'uppercase';
  }, [selectedElement?.style?.textTransform]);

  const currentFontSize = useMemo(() => {
    const fontSize = selectedElement?.style?.fontSize || 24;
    return typeof fontSize === 'number' ? fontSize : parseInt(String(fontSize)) || 24;
  }, [selectedElement?.style?.fontSize]);

  const currentColor = useMemo(() => {
    return selectedElement?.style?.color || '#ffffff';
  }, [selectedElement?.style?.color]);

  if (!selectedElement) {
    return null;
  }

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="text-element-typography-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <Icon name="IconTypography" size={16} />
              <PlayerMenuTooltip content="Typography" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            afterLeave={() => setActiveSubPicker(null)}
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="rounded-lg border bg-white shadow-lg">
                {usePlatformBrandKit && activeSubPicker === 'font' ? (
                  <FontSelector
                    isInline={true}
                    fontTarget={SelectionElementType.TEXT}
                    onChange={onFontFamilyChange}
                    selectedFontName={selectedElement.details?.font?.name}
                    selectedFontLocation={selectedElement.details?.font?.location}
                    inline
                    onBack={() => setActiveSubPicker(null)}
                  />
                ) : usePlatformBrandKit && activeSubPicker === 'color' ? (
                  <ColorPicker
                    defaultColorsLabel="Text Color"
                    onChange={onTextColorChange}
                    hex={currentColor}
                    inline
                    onBack={() => setActiveSubPicker(null)}
                  />
                ) : (
                  <div className="space-y-4 p-4">
                    {/* Font Family */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-3 text-sm font-medium">Font</span>
                      <div className="group/button relative col-span-9 cursor-pointer rounded-md text-sm">
                        <FontSelector
                          isInline={true}
                          fontTarget={SelectionElementType.TEXT}
                          onChange={onFontFamilyChange}
                          selectedFontName={selectedElement.details?.font?.name}
                          selectedFontLocation={selectedElement.details?.font?.location}
                          onOpen={usePlatformBrandKit ? () => setActiveSubPicker('font') : undefined}
                        />
                      </div>
                    </div>

                    {/* Font color */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-3 text-sm font-medium">Color</span>
                      <ColorPicker
                        defaultColors={brandKitStore.colors}
                        defaultColorsLabel="Text Color"
                        onChange={onTextColorChange}
                        hex={currentColor}
                        onOpen={usePlatformBrandKit ? () => setActiveSubPicker('color') : undefined}
                        children={
                          <div className="flex size-full items-center justify-between bg-white p-2">
                            <div
                              className="size-6 rounded border"
                              style={{
                                background: currentColor
                              }}
                            />
                            <span className="font-mono text-xs text-slate-400">{currentColor}</span>
                          </div>
                        }
                        classNames="relative col-span-9 flex w-full items-center justify-between rounded-md border overflow-hidden"
                      />
                    </div>

                    {/* Font Size */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-3 text-sm font-medium">Size</span>
                      <span className="col-span-9 flex items-center gap-2">
                        <input
                          type="range"
                          min={12}
                          max={120}
                          value={currentFontSize}
                          step={1}
                          onChange={onFontSizeChange}
                          className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                        />
                        <span className="ml-1 w-8 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                          {currentFontSize}px
                        </span>
                      </span>
                    </div>

                    {/* Case */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-3 text-sm font-medium">Uppercase</span>
                      <div className="group/button relative col-span-9 flex items-center justify-end rounded-md text-sm">
                        <Toggle on={isCapsLock} onToggle={toggleTextCaps} />
                      </div>
                    </div>
                    {/* Text Content */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-3 text-sm font-medium">Text</span>
                      <div className="col-span-9">
                        <textarea
                          value={inputText}
                          onChange={onTextChange}
                          rows={2}
                          className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Enter text content..."
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(TextElementTypographyPopover);
